import React from 'react';
import { useBusinessInfo } from '../../lib/Queries';
import * as Toast from '../../lib/Toast.js';
import Loading from '../Loading';

const ReferralSettings = () => {
    const { isLoading, data } = useBusinessInfo();
    const { business, referral } = data || {};

    if (isLoading || !referral) {
        return <Loading />;
    }

    const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(referral.link);
                Toast.success('Referral link copied!');
            } catch (err) {
                console.error('Failed to copy:', err);
                Toast.error('Failed to copy link to clipboard');
            }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Join Breely',
                    text: 'Check out Breely for managing your business!',
                    url: referral.link,
                });
            } catch (err) {
                // User cancelled or error occurred
                if (err.name !== 'AbortError') {
                    console.error('Error sharing:', err);
                }
            }
        } else {
            // Fallback to copy
            handleCopy();
        }
    };

    return (
        <div className="max-w-2xl">
            <div className="mb-8">
                <p className="">
                    Share Breely with other businesses and you'll both get rewarded!
                    You'll get 5 extra clients on the free plan for each business you refer,
                    and they'll get 5 extra clients too (up to a max of 50 total).
                </p>
            </div>

            <div className="mb-6">
                <div className="input-label">Your Referral Link</div>
                <div className="mb-2">
                    <input
                        type="text"
                        className="w-full mb-2"
                        readOnly={true}
                        value={referral?.link}
                    />
                    <div className="text-gray-500 text-[12px] ml-2 mb-3">
                        Share this link with other businesses. When they sign up, you'll both get 5 extra clients on the free plan!
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn-light-pill w-full xs:w-auto"
                            onClick={handleCopy}
                        >
                            Copy Link
                        </button>
                        <button
                            type="button"
                            className="btn-light-pill w-full xs:w-auto"
                            onClick={handleShare}
                        >
                            Share
                        </button>
                    </div>
                </div>
            </div>

            <div className="mb-6">
                <div className="input-label">Referral Stats</div>
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700">Businesses you've referred:</span>
                        <span className="font-semibold text-lg">{referral.count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-700">Extra clients earned:</span>
                        <span className="font-semibold text-lg text-green-600">
                            +{Math.min(40, referral.count * 5)}
                        </span>
                    </div>
                </div>
            </div>

            {referral.wasReferred && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <svg
                            className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <div>
                            <div className="font-semibold text-blue-900 mb-1">
                                You were referred!
                            </div>
                            <div className="text-blue-800 text-sm">
                                You received +5 extra clients on the free plan because you were referred by another business.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReferralSettings;

